import type { routes } from '@semios/app-platform-banyan-route-definitions'
import { DropdownSelectorProperty } from 'App/Map/PanelDetails/SectionTitleBars/DropdownSelectorProperty/DropdownSelectorProperty'
import {
  SprayConditionsInfo,
  type TSprayConditionsRulesForARegion,
} from 'App/Map/PanelDetails/SectionTitleBars/SprayConditionsTitleChildren/SprayConditionsInfo'
import { propertyLacksPermissionSectionMaker } from 'App/Map/PanelDetails/_utils/propertyLacksPermissionSectionMaker'
import { selectedPropertyHasPermission } from 'App/Map/PanelDetails/_utils/selectedPropertyHasPermission'
import { getSprayConditionsTitleAndColor } from 'App/Map/_utils/getSprayConditionsTitleAndColor'
import type {
  ESprayRecommendation,
  TValuesTimeseriesBlocks,
  TValuesTimeseriesProperties,
} from 'App/Map/types'
import type { GridTableContentSection, GridTableContentSectionItem } from 'components/GridTable/types'
import { translate } from 'i18n/i18n'
import { isEmpty } from 'lodash'
import { fieldAssetStore } from 'stores/fieldAssetStore/fieldAssetStore'
import type { TSelectedFieldAssetsStoreState } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import { selectedFieldAssetsStore } from 'stores/selectedFieldAssetsStore/selectedFieldAssetsStore'
import type { selectedValueGroupsStore } from 'stores/selectedValueGroupsStore/selectedValueGroupsStore'
import { doesSelectedBlockHaveValueTypes } from 'utils/doesSelectedFieldAssetHaveValueTypes'
import { sortByKey } from 'utils/sortByKey'
import { BadgeCell } from '../../../BadgeCell/BadgeCell'
import { defaultValuesRowHeight, timestampToValuesDictionaryMaker } from '../_utils'

const checkPermission = () => selectedPropertyHasPermission({ permission: 'VIEW_SPRAY_FORECAST' })

export const apiArgs = ({
  selectedValueGroups,
  selectedFieldAssets,
}: {
  selectedValueGroups: ReturnType<typeof selectedValueGroupsStore.getState>['selectedValueGroups']
  selectedFieldAssets: TSelectedFieldAssetsStoreState
}): Partial<routes.Values.Request> => {
  if (!checkPermission()) return {}

  const blocksForProperty =
    fieldAssetStore.getState()?.properties?.[Number(selectedFieldAssets.property)]?.blocks

  if (!selectedValueGroups.spray_conditions || isEmpty(blocksForProperty)) {
    return {}
  }

  const blockIds = Object.keys(blocksForProperty).map(Number)

  const preferredAggregationInterval = {
    preferredAggregationInterval: 'DAILY',
  }

  const valuesRequested: Record<string, typeof preferredAggregationInterval> = {
    sprayConditions: preferredAggregationInterval,
  }

  if (
    !doesSelectedBlockHaveValueTypes({
      valuesTimeseries: Object.keys(valuesRequested) as TValuesTimeseriesBlocks[],
    })
  )
    return {}

  return {
    blocks: {
      blockIds,
      valuesRequested,
    },
  }
}

export const content = ({ data }: { data: routes.Values.Response }): GridTableContentSection => {
  const commonReturnItems = {
    title: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{translate.phrases.banyanApp('Spray Conditions')}</span>
        <SprayConditionsInfo valuesTimeseriesToFilterOn={['sprayConditions']} />
      </div>
    ),
    fieldAssetPicker: (
      <DropdownSelectorProperty
        valuesTimeseriesToFilterOn={['sprayConditions'] as unknown as TValuesTimeseriesProperties[]}
      />
    ),
    id: 'summary-grid-spray-conditions',
  }

  if (!checkPermission()) return propertyLacksPermissionSectionMaker(commonReturnItems)

  const { property } = selectedFieldAssetsStore.getState()
  const blocksForProperty = fieldAssetStore.getState()?.properties?.[Number(property)]?.blocks ?? {}

  /**
   * every block should have the same sprayConditionsRules,
   * since they're all from the same property, so when we're
   * looping over them, let's pick the first rules we find
   */
  let sprayConditionsRules: TSprayConditionsRulesForARegion | undefined = undefined

  const items: GridTableContentSectionItem[] = Object.values(blocksForProperty)
    .sort(sortByKey('name'))
    .map((block) => {
      const { metadata, timeseries } = data?.blocks?.[block.blockId]?.values?.sprayConditions?.[0] ?? {}

      if (!sprayConditionsRules && metadata?.sprayConditionsRules) {
        sprayConditionsRules = metadata?.sprayConditionsRules
      }

      return {
        id: String(block.blockId),
        height: defaultValuesRowHeight,
        labelMinWidth: 190,
        valueMinWidth: 160,
        label: block.name ?? translate.phrases.banyanApp('Unnamed Block'),
        render: (dataPoint?: { value: ESprayRecommendation }) => {
          if (dataPoint?.value === undefined) return translate.phrases.templates('-')

          const { color, name } = getSprayConditionsTitleAndColor(dataPoint?.value ?? null)

          return <BadgeCell backgroundColor={color}>{name}</BadgeCell>
        },
        // TODO: because timeseries isn't typed, this next call doesn't throw ts errors, but value-definitions should fix this
        values: timeseries ? timestampToValuesDictionaryMaker(timeseries) : {},
      }
    })

  return {
    ...commonReturnItems,
    items,
    title: (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span>{translate.phrases.banyanApp('Spray Conditions')}</span>
        <SprayConditionsInfo
          sprayConditionsRules={sprayConditionsRules}
          valuesTimeseriesToFilterOn={['sprayConditions']}
        />
      </div>
    ),
  }
}
